import React, { useState, useEffect, useRef } from 'react';
import './PricingDetailNavbar.css';
import merubotIcon from '../../../assets/images/merubot_icon.png';
import { useNavigate } from 'react-router-dom';

// Dynamically using the plans matching your PricingDetail page
const NAV_LINKS = [
  { label: 'Starter', id: 'pricing', planParam: 'starter' },
  { label: 'Basic', id: 'pricing', planParam: 'basic' },
  { label: 'Standard', id: 'pricing', planParam: 'standard' },
  { label: 'Premium', id: 'pricing', planParam: 'premium' },
];

const SCROLL_OFFSETS = {
  pricing: -90, // Scrolls past the #pricing div top to keep headings cleanly visible
  default: 0,
};

export default function Navbar({ onDemoClick }) {
  const [scrolled, setScrolled] = useState(false);
  const [visible, setVisible] = useState(true);
  const [active, setActive] = useState('');
  const [menuOpen, setMenuOpen] = useState(false);

  const lastScrollY = useRef(0);
  const scrollUpDistance = useRef(0);
  const navigate = useNavigate();

  useEffect(() => {
    // 1. Safe Intersection Observer configuration
    const el = document.getElementById('pricing');
    let obs;
    
    if (el) {
      obs = new IntersectionObserver(
        ([entry]) => {
          if (entry.isIntersecting) {
            setActive('pricing');
          } else {
            setActive('');
          }
        },
        { threshold: 0.1 }
      );
      obs.observe(el);
    }

    // 2. Scroll Listener (Now runs independently, even if '#pricing' doesn't exist)
    const onScroll = () => {
      const currentY = window.scrollY;
      setScrolled(currentY > 40);

      if (currentY < 80) {
        setVisible(true);
        scrollUpDistance.current = 0;
      } else if (currentY < lastScrollY.current) {
        scrollUpDistance.current += lastScrollY.current - currentY;
        if (scrollUpDistance.current > 300) {
          setVisible(true);
          scrollUpDistance.current = 0;
        }
      } else if (currentY > lastScrollY.current + 6) {
        setVisible(false);
        setMenuOpen(false);
        scrollUpDistance.current = 0;
      }

      lastScrollY.current = currentY;
    };

    const onMouseMove = (e) => {
      if (e.clientY < 80) setVisible(true);
    };

    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('mousemove', onMouseMove, { passive: true });

    return () => {
      if (obs) obs.disconnect();
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('mousemove', onMouseMove);
    };
  }, []);

  const handlePlanClick = (id, planParam) => {
    const el = document.getElementById(id);
    
    if (el) {
      // If we are on the home landing page where #pricing exists, scroll to it smoothly
      const extraOffset = SCROLL_OFFSETS[id] ?? SCROLL_OFFSETS.default;
      const top = el.getBoundingClientRect().top + window.scrollY - extraOffset;
      window.scrollTo({ top, behavior: 'smooth' });
    } else {
      // If we are on a different page, navigate cleanly to the pricing route with the correct active plan
      navigate(`/pricing-detail?plan=${planParam}`);
    }
    setMenuOpen(false);
  };

  return (
    <>
      <nav className={[
        'navbar',
        scrolled ? 'navbar--scrolled' : '',
        !visible ? 'navbar--hidden' : '',
      ].filter(Boolean).join(' ')}>

        {/* Logo icon — scrolls to hero/top */}
        {/* <button className="nav-logo-btn" onClick={() => handlePlanClick('hero', '')} aria-label="Go to top">
          <img src={merubotIcon} alt="merubot" className="nav-logo-icon" />
        </button> */}

        {/* <div className="nav-divider" /> */}

        <ul className="nav-links">
          {NAV_LINKS.map(({ label, id, planParam }) => (
            <li key={label}>
              <button
                className="nav-link-btn"
                onClick={() => handlePlanClick(id, planParam)}
              >
                {label}
              </button>
            </li>
          ))}
        </ul>

        {/* <div className="nav-divider" />

        <button className="nav-cta" onClick={() => handlePlanClick('demo-form', '')}>
          Request demo
        </button>
        <button className="nav-cta" onClick={() => navigate('/email/redirect')}>
          Sign In
        </button> */}

        {/* Hamburger — mobile only */}
        <button
          className={`hamburger${menuOpen ? ' open' : ''}`}
          onClick={() => setMenuOpen(o => !o)}
          aria-label="Toggle menu"
        >
          <span /><span /><span />
        </button>

      </nav>

      {/* Mobile dropdown */}
      {menuOpen && (
        <div className="mobile-menu">
          <button className="mobile-link" onClick={() => handlePlanClick('hero', '')}>
            🏠 Home
          </button>
          {NAV_LINKS.map(({ label, id, planParam }) => (
            <button key={label} className="mobile-link" onClick={() => handlePlanClick(id, planParam)}>
              {label}
            </button>
          ))}
          <button
            className="mobile-cta"
            onClick={() => { handlePlanClick('demo-form', ''); setMenuOpen(false); }}
          >
            Request Demo
          </button>
          <button
            className="mobile-cta"
            onClick={() => { navigate('/login'); setMenuOpen(false); }}
          >
            Sign In
          </button>
        </div>
      )}
    </>
  );
}